const API_BASE = process.env.REACT_APP_API_URL || '/api';

export type CopilotCitation = {
  type: 'consent' | 'article' | 'source';
  reference: string;
  label: string;
  route: string;
  entity_id?: number | null;
};

export type CopilotScope = {
  project_name?: string;
  project_code?: string;
  user_name?: string;
  role?: string;
  as_at?: string;
};

export type CopilotAnswer = {
  answer_id: string;
  summary: string;
  bullets: string[];
  citations: CopilotCitation[];
  follow_ups: string[];
  scope: CopilotScope;
  intent: string;
  mode: 'model' | 'offline';
  notes: string[];
};

export type CopilotTurn = { role: 'user' | 'assistant'; content: string };

const scoped = (path: string, projectId: number | null) =>
  `${API_BASE}${path}${projectId ? `${path.includes('?') ? '&' : '?'}project_id=${projectId}` : ''}`;

async function parse(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || payload.error || `Request failed (${response.status})`);
  }
  return payload;
}

export async function fetchSuggestions(projectId: number | null, page?: string): Promise<{ suggestions: string[]; scope: CopilotScope }> {
  const path = page ? `/copilot/suggestions?page=${encodeURIComponent(page)}` : '/copilot/suggestions';
  const response = await fetch(scoped(path, projectId), { credentials: 'include' });
  return parse(response);
}

export async function askCopilot(question: string, projectId: number | null, history: CopilotTurn[]): Promise<CopilotAnswer> {
  const response = await fetch(scoped('/copilot/ask', projectId), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question, history: history.slice(-6) }),
  });
  return parse(response);
}

export function sendFeedback(answerId: string, rating: 'helpful' | 'unhelpful', projectId: number | null): void {
  fetch(scoped('/copilot/feedback', projectId), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answer_id: answerId, rating }),
  }).catch(() => undefined);
}
